import "./modal.scss";

import { delay, isNil } from "lodash";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import Dimensions from "../../constants/styles/dimensions";
import { PropTypeShapes } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { withContainerContext } from "../container/ContainerContext";
import NavigationToolbar from "../navigationToolbar";
import { asPortalChild } from "../portal/asPortalChild";
import ModalBody from "./ModalBody";
import { ModalResizeContext } from "./ModalResizeContext";
import { ModalSizeContext, withModalSizeContext } from "./ModalSizeContext";
import ModalTabBar from "./ModalTabBar";

const isIos = preprocessorQuery.isIos();

const getModalWrapperHeights = memoizeOne((maxHeight, fixedHeight) => {
  let SCALED_MODAL_TAB_BAR_HEIGHT = isIos ? Dimensions.TOOLBAR_HEIGHT : Dimensions.MODAL_TAB_BAR_HEIGHT;
  const maxWrapperHeight = maxHeight - SCALED_MODAL_TAB_BAR_HEIGHT - Dimensions.MODAL_TAB_BAR_MARGIN;
  const fixedWrapperHeight = fixedHeight
    ? fixedHeight - SCALED_MODAL_TAB_BAR_HEIGHT - Dimensions.MODAL_TAB_BAR_MARGIN
    : null;
  return { maxHeight: maxWrapperHeight, fixedHeight: fixedWrapperHeight };
});

class ModalTabView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    //#!if iOS === true
// this.state = { activeTab: !isNil(this.props.activeTab) ? this.props.activeTab : null };
    //#!else
    this.state = { activeTab: !isNil(this.props.activeTab) ? this.props.activeTab : 0 };
    //#!endif
    this.fadeContentIn = this.fadeContentIn.bind(this);
    this.updateViewDimensions = this.updateViewDimensions.bind(this);
    this._modalResizeContextValue = { triggerResize: this.updateViewDimensions };
  }

  componentDidMount() {
    this.updateViewDimensions();
  }

  componentDidUpdate(prevProps) {
    this.updateViewDimensions();
    if (this.props.activeTab !== prevProps.activeTab) {
      this.updateActiveTab();
    }
  }

  updateActiveTab() {
    this.setState({ activeTab: this.props.activeTab });
  }

  updateViewDimensions() {
    if (!this.props.fixedHeight) {
      const measureRect = this._measureNode.getBoundingClientRect();
      const newHeight = Math.min(this.props.maxHeight, measureRect.height);
      this._domNode.style.height = `${newHeight}px`;
    }
    delay(this.fadeContentIn, 100);
  }

  fadeContentIn() {
    //#!if iOS !== true
    if (this._wrapperNode) {
      this._wrapperNode.style.transition = "opacity 0.25s ease-in-out";
      this._wrapperNode.style.opacity = "1";
    }
    //#!endif
  }

  tabClick(index) {
    if (index !== this.state.activeTab) {
      //#!if iOS !== true
      this._wrapperNode.style.transition = null;
      this._wrapperNode.style.opacity = "0";
      //#!endif

      this.setState({ activeTab: index });

      if (this.props.onTabClick) {
        this.props.onTabClick(index);
      }
    }
  }

  renderTabBar() {
    return (
      <ModalTabBar
        key="modalTabBar"
        tabs={this.props.tabs}
        disableTabSwitch={this.props.disableTabSwitch}
        activeTab={this.state.activeTab}
        onClick={this.tabClick.bind(this)}
      />
    );
  }

  renderTabWrapper() {
    const modalWrapperHeights = getModalWrapperHeights(this.props.maxHeight, this.props.fixedHeight);

    const { maxHeight, fixedHeight } = modalWrapperHeights;

    const style = {
      maxHeight: `${maxHeight}px`,
      height: fixedHeight ? `${fixedHeight}px` : null,
      paddingLeft: `${this.props.paddingLeft}px`,
      paddingRight: `${this.props.paddingRight}px`
    };
    return (
      <ModalSizeContext.Provider key="modalSizeContextProvider" value={modalWrapperHeights}>
        <div key="modalTabWrapper" className="modalTabWrapper" ref={node => (this._wrapperNode = node)} style={style}>
          {this.props.tabs[this.state.activeTab].view}
        </div>
      </ModalSizeContext.Provider>
    );
  }

  renderPortal() {
    return asPortalChild(
      <div className={`iosModalContentWrap`}>
        <NavigationToolbar
          title={this.props.tabs[this.state.activeTab].label}
          showBack={true}
          showBackArrow={true}
          onBackClick={() => this.tabClick(null)}
          backText={this.props.backText ? this.props.backText : "Back"}
          showNext={true}
          onNextClick={!this.props.doneDisabled ? this.props.onDoneClick : null}
          nextText={"Done"}
          nextDisabled={this.props.doneDisabled}
          flexLeft={1}
          flexCenter={3}
          flexRight={1}
        />
        <ModalBody>{this.props.tabs[this.state.activeTab].view}</ModalBody>
        {this.props.footer ? this.props.footer : null}
      </div>
    );
  }

  render() {
    const tabBar = this.renderTabBar();

    let selectedTabView = null;

    // #!if iOS === true
// selectedTabView = !isNil(this.state.activeTab) ? this.renderPortal() : null;
    // #!else
    selectedTabView = this.renderTabWrapper();
    // #!endif

    const style = {
      maxHeight: `${this.props.maxHeight}px`,
      height: this.props.fixedHeight ? `${this.props.fixedHeight}px` : null
    };
    return (
      <ModalResizeContext.Provider value={this._modalResizeContextValue}>
        <div className="modalTabView" ref={node => (this._domNode = node)} style={style}>
          <div className="modalTabMeasure" ref={node => (this._measureNode = node)}>
            {tabBar}
            {selectedTabView}
          </div>
        </div>
      </ModalResizeContext.Provider>
    );
  }
}

ModalTabView.propTypes = {
  tabs: PropTypeShapes.inspectorTabArray.isRequired,
  disableTabSwitch: PropTypes.bool,
  backText: PropTypes.string,
  activeTab: PropTypes.number,

  // From ModalSizeContext
  maxHeight: PropTypes.number.isRequired,
  fixedHeight: PropTypes.number,

  onTabClick: PropTypes.func,

  //#!if iOS === true
// onDoneClick: PropTypes.func.isRequired,
// doneDisabled: PropTypes.bool,
// footer: PropTypes.element,
  //#!endif

  // from withContainerContext
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

export default withContainerContext(withModalSizeContext(ModalTabView));
